// /apps/patient/src/pages/progress/ProgressPage.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { Calendar, Filter, ChevronDown } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { ProgressStatsCards, ProgressSummary } from './components/ProgressStatsCards';
import { SessionCard, SessionData } from './components/SessionCard';

type DateRange = 'this_week' | 'this_month' | 'all_time' | 'custom';

interface PaginationInfo {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export function ProgressPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('this_month');
  const [assignments, setAssignments] = useState<any[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const getDateRangeParams = useCallback(() => {
    const today = new Date();
    switch (dateRange) {
      case 'this_week':
        return {
          start_date: format(startOfWeek(today), 'yyyy-MM-dd'),
          end_date: format(endOfWeek(today), 'yyyy-MM-dd')
        };
      case 'this_month':
        return {
          start_date: format(subDays(today, 30), 'yyyy-MM-dd'),
          end_date: format(today, 'yyyy-MM-dd')
        };
      case 'all_time':
      default:
        return {};
    }
  }, [dateRange]);

  const fetchAssignments = useCallback(async () => {
    try {
      const data = await patientApiService.getMyExercisePrograms();
      if (Array.isArray(data)) {
        setAssignments(data.filter(a => a.status === 'active' || a.status === 'completed'));
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const params = getDateRangeParams();
      const data = await patientApiService.getProgressSummary(params);
      setSummary(data);
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      // Set default values on error
      setSummary({
        total_sessions: 0,
        total_exercises: 0,
        total_minutes: 0,
        current_streak: 0,
        sessions_this_week: 0,
        last_session_date: null
      });
    }
  }, [getDateRangeParams]);

  const fetchSessions = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params: any = {
        ...getDateRangeParams(),
        page,
        per_page: 20
      };

      if (selectedAssignment !== 'all') {
        params.assignment_id = selectedAssignment;
      }

      const data = await patientApiService.getProgressSessions(params);

      if (append) {
        setSessions(prev => [...prev, ...(data.sessions || [])]);
      } else {
        setSessions(data.sessions || []);
      }

      setPagination({
        page: data.page || 1,
        per_page: data.per_page || 20,
        total: data.total || 0,
        total_pages: data.total_pages || 1
      });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      if (!append) {
        setSessions([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [getDateRangeParams, selectedAssignment]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    fetchSummary();
    fetchSessions(1, false);
  }, [dateRange, selectedAssignment, fetchSummary, fetchSessions]);

  const loadMore = () => {
    if (pagination && pagination.page < pagination.total_pages) {
      fetchSessions(pagination.page + 1, true);
    }
  };

  const handleSessionClick = (sessionId: string) => {
    navigate(`/progress/session/${sessionId}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Progress" />

      <div className="space-y-6">
        {/* Stats Cards */}
        <ProgressStatsCards summary={summary} loading={loading} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Session History</h3>

            <div className="flex items-center gap-3">
              {/* Date Range Filter */}
              <div className="relative">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as DateRange)}
                  className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="this_week">This Week</option>
                  <option value="this_month">This Month</option>
                  <option value="all_time">All Time</option>
                </select>
                <Calendar className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Program Filter */}
              {assignments.length > 1 && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${
                    showFilters || selectedAssignment !== 'all'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Filter className="h-4 w-4" />
                  Filter
                  {selectedAssignment !== 'all' && (
                    <span className="bg-white/20 px-1.5 rounded">1</span>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && assignments.length > 1 && (
            <div className="mt-4 pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program
              </label>
              <select
                value={selectedAssignment}
                onChange={(e) => setSelectedAssignment(e.target.value)}
                className="w-full sm:w-auto appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Programs</option>
                {assignments.map((assignment) => (
                  <option key={assignment.id} value={assignment.id}>
                    {assignment.workout_plan?.name ||
                      assignment.rehab_program?.name ||
                      assignment.exercise_prescription?.name ||
                      'Program'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Session List */}
        <div className="space-y-3">
          {loading ? (
            // Loading skeletons
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />
                      <div className="h-2 bg-gray-200 rounded w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-white rounded-lg border p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No sessions yet</h4>
              <p className="text-gray-600">
                Complete your first workout to start tracking your progress!
              </p>
            </div>
          ) : (
            <>
              {sessions.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  onClick={() => handleSessionClick(session.id)}
                />
              ))}

              {/* Load More Button */}
              {pagination && pagination.page < pagination.total_pages && (
                <div className="text-center pt-4">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <ChevronDown className="h-4 w-4" />
                        Load More ({pagination.total - sessions.length} remaining)
                      </span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
